import {
  useContext,
  createContext,
  FC,
  useCallback,
  useEffect,
  useState,
  useRef,
} from 'react';
import AIChatService from '__services/AIChatService';
import {
  AIChatContextValue,
  AIChatProviderProps,
  CreateSessionPayload,
} from './types';
import { SESSION_STORAGE_KEY, TURN_STORAGE_KEY_PREFIX } from './constants';
import { useChatStream } from './useChatStream';
import { useSessionStorage } from 'hooks/useSessionStorage';
import { getTimezoneOffset } from '__services/utils';
export const ChatBotV2Context = createContext<AIChatContextValue | null>(null);

export const ChatBotV2Provider: FC<AIChatProviderProps> = ({
  children,
  config,
  sessionId: initialSessionId,
  autoConnect = true,
  initialMessage,
  freshSession = false,
  onError,
  onMessageComplete,
  onSessionCreated,
}) => {
  const [sessionId, setSessionId] = useState<string | null>(
    initialSessionId || null
  );
  const [isLoadingHistory, setIsLoadingHistory] = useState(
    autoConnect && !initialSessionId
  );

  const { getItem, setItem, removeItem } = useSessionStorage();

  const hasInitializedRef = useRef(false);
  const isRestoringFromStorageRef = useRef(false);
  const sessionCreatedRef = useRef(false);
  const onErrorRef = useRef(onError);
  const initialMessageRef = useRef(initialMessage);
  onErrorRef.current = onError;

  const {
    messages,
    quickResponseData,
    placeholder,
    turnCount,
    isStreaming,
    connectionState,
    alert,
    previewData,
    sendMessage: streamSendMessage,
    rehydrate,
    resumeStream,
    abort,
    setWelcomeMessage,
  } = useChatStream({
    sessionId,
    onError,
    onComplete: onMessageComplete,
  });

  const createSession = useCallback(
    async (payload?: CreateSessionPayload): Promise<string> => {
      const sessionPayload = {
        filter_timezone_offset: getTimezoneOffset(),
        ...(payload || {
          graph: config.graph,
          tag: config.tag,
          ...config.metadata,
        }),
      };

      let response;
      try {
        response = await AIChatService.createSession(sessionPayload);
      } catch (error: any) {
        const detail = error?.response?.data?.detail;
        throw detail ? new Error(detail) : error;
      }

      const newSessionId = response.session_id;

      sessionCreatedRef.current = true;
      setSessionId(newSessionId);

      setItem(SESSION_STORAGE_KEY, newSessionId);

      if (response.welcome_message) {
        setWelcomeMessage(response.welcome_message);
      }

      onSessionCreated?.(newSessionId);
      return newSessionId;
    },
    [config, onSessionCreated, setItem, setWelcomeMessage]
  );

  const sendMessage = useCallback(
    async (text: string): Promise<void> => {
      let currentSessionId = sessionId;

      if (!currentSessionId) {
        try {
          currentSessionId = await createSession();
        } catch (error) {
          onErrorRef.current?.(error as Error);
          return;
        }
      }
      streamSendMessage(text, currentSessionId);
    },
    [sessionId, createSession, streamSendMessage]
  );

  const handleQuickResponseSelect = useCallback(
    (value: string, type: string, url?: string) => {
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }

      if (type === 'single_select' || type === 'action') {
        sendMessage(value);
      }
    },
    [sendMessage]
  );

  const reconnect = useCallback(async (): Promise<void> => {
    if (!sessionId) return;

    try {
      const historyMessages = await AIChatService.getMessages(sessionId);
      rehydrate(historyMessages);
    } catch (error) {
      onError?.(error as Error);
    }
  }, [sessionId, rehydrate, onError]);

  const resetChat = useCallback((): void => {
    abort();
    setSessionId(null);
    setIsLoadingHistory(false);
    hasInitializedRef.current = false;
    isRestoringFromStorageRef.current = false;
    sessionCreatedRef.current = false;

    removeItem(SESSION_STORAGE_KEY);
  }, [abort, removeItem]);

  // Auto-connect on mount if sessionId provided (but not if restoring from storage or just created)
  useEffect(() => {
    if (
      !autoConnect ||
      !initialSessionId ||
      isRestoringFromStorageRef.current ||
      sessionCreatedRef.current
    ) {
      sessionCreatedRef.current = false;
      return;
    }

    const load = async () => {
      setSessionId(initialSessionId);
      try {
        // Inline REST call using initialSessionId to avoid the reconnect → sessionId dep cycle
        const msgs = await AIChatService.getMessages(initialSessionId);
        rehydrate(msgs);
      } catch (err) {
        onErrorRef.current?.(err as Error);
      }
    };

    sessionCreatedRef.current = false;
    load();
  }, [autoConnect, initialSessionId, rehydrate]);

  // Check for existing session on mount, or create new one.
  useEffect(() => {
    if (!initialSessionId && autoConnect && !hasInitializedRef.current) {
      hasInitializedRef.current = true;

      const resumeOrInitializeSession = async () => {
        try {
          const storedSessionId = freshSession
            ? null
            : getItem(SESSION_STORAGE_KEY);
          if (storedSessionId) {
            isRestoringFromStorageRef.current = true;
            setSessionId(storedSessionId);

            const storedTurn = getItem(
              `${TURN_STORAGE_KEY_PREFIX}${storedSessionId}`
            );

            if (storedTurn) {
              resumeStream(storedSessionId);
              setIsLoadingHistory(false);
            } else {
              setIsLoadingHistory(true);
              try {
                const historyMessages =
                  await AIChatService.getMessages(storedSessionId);
                if (!historyMessages?.length) {
                  removeItem(SESSION_STORAGE_KEY);
                  isRestoringFromStorageRef.current = false;
                  try {
                    const newSessionId = await createSession();
                    if (initialMessageRef.current) {
                      streamSendMessage(
                        initialMessageRef.current,
                        newSessionId
                      );
                      initialMessageRef.current = undefined;
                    }
                  } catch (sessionError) {
                    onErrorRef.current?.(sessionError as Error);
                  } finally {
                    setIsLoadingHistory(false);
                  }
                  return;
                }
                rehydrate(historyMessages);
                setIsLoadingHistory(false);
              } catch (error) {
                removeItem(SESSION_STORAGE_KEY);
                isRestoringFromStorageRef.current = false;
                setIsLoadingHistory(false);
                onErrorRef.current?.(error as Error);
                try {
                  await createSession();
                } catch (sessionError) {
                  onErrorRef.current?.(sessionError as Error);
                }
              }
            }
          } else {
            try {
              setIsLoadingHistory(true);
              const newSessionId = await createSession();
              if (initialMessageRef.current) {
                streamSendMessage(initialMessageRef.current, newSessionId);
                initialMessageRef.current = undefined;
              }
            } catch (error) {
              onErrorRef.current?.(error as Error);
            } finally {
              setIsLoadingHistory(false);
            }
          }
        } catch (error) {
          try {
            await createSession();
          } catch (sessionError) {
            onErrorRef.current?.(sessionError as Error);
          }
        }
      };

      resumeOrInitializeSession();
    }
  }, [
    initialSessionId,
    autoConnect,
    freshSession,
    streamSendMessage,
    resumeStream,
    rehydrate,
    createSession,
    getItem,
    removeItem,
  ]);

  const value: AIChatContextValue = {
    messages,
    quickResponseData,
    placeholder,
    connectionState,
    isStreaming,
    isLoadingHistory,
    sessionId,
    turnCount,
    config,
    alert,
    previewData,
    sendMessage,
    handleQuickResponseSelect,
    createSession,
    reconnect,
    resetChat,
  };

  return (
    <ChatBotV2Context.Provider value={value}>
      {children}
    </ChatBotV2Context.Provider>
  );
};

export const useChat = (): AIChatContextValue => {
  const context = useContext(ChatBotV2Context);
  if (!context) {
    throw new Error('useChat must be used within a ChatBotV2Provider');
  }
  return context;
};
